// Incoming-from-1C queue: unmatched 1C orders that admin must attach, create-client, or ignore.
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { RefreshCw, User, Building2, Phone, MapPin, Search, UserPlus, Link2, X, Package, Inbox, Download, FileCode, ClipboardCopy } from 'lucide-react';

function formatCurrency(v) {
  const n = Number(v || 0);
  return `${n.toLocaleString('ru-RU')} ₸`;
}

export default function IncomingOrders1C() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');

  // Attach dialog
  const [attachTarget, setAttachTarget] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/1c/incoming`, {
        params: { status: statusFilter },
        headers: getAuthHeaders(),
      });
      setRows(resp.data.items || []);
    } catch (e) {
      toast.error('Не удалось загрузить очередь');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [statusFilter]);

  const searchUsers = async (entryId, q) => {
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const resp = await axios.get(`${API}/1c/incoming/${entryId}/search-users`, {
        params: { q },
        headers: getAuthHeaders(),
      });
      setSearchResults(resp.data.users || []);
    } catch (e) {
      toast.error('Ошибка поиска');
    } finally {
      setSearching(false);
    }
  };

  const doAttach = async (userId) => {
    if (!attachTarget) return;
    try {
      const resp = await axios.post(
        `${API}/1c/incoming/${attachTarget.id}/attach`,
        { user_id: userId },
        { headers: getAuthHeaders() }
      );
      toast.success(`Заказ ${resp.data.order_number} привязан`);
      setAttachTarget(null);
      setSearchQ('');
      setSearchResults([]);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка привязки');
    }
  };

  const doCreateClient = async (entry) => {
    if (!window.confirm(`Создать нового клиента (${entry.role_hint}) из этой накладной?`)) return;
    try {
      const resp = await axios.post(
        `${API}/1c/incoming/${entry.id}/create-client`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Клиент создан: ${resp.data.user_email}. Заказ ${resp.data.order_number} привязан.`);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка создания клиента');
    }
  };

  const doIgnore = async (entry) => {
    if (!window.confirm('Проигнорировать эту накладную из 1С?')) return;
    try {
      await axios.post(
        `${API}/1c/incoming/${entry.id}/ignore`,
        { reason: 'ignored by admin' },
        { headers: getAuthHeaders() }
      );
      toast.success('Отправлено в игнор');
      fetchRows();
    } catch (e) {
      toast.error('Ошибка');
    }
  };

  const badge = (status) => {
    if (status === 'pending') return <Badge className="bg-orange-100 text-orange-800">Ожидает</Badge>;
    if (status === 'attached') return <Badge className="bg-emerald-100 text-emerald-800">Привязан</Badge>;
    return <Badge className="bg-gray-100 text-gray-700">Пропущен</Badge>;
  };

  const download1CFile = async (kind) => {
    // kind: 'instructions' | 'bsl'
    try {
      const resp = await axios.get(`${API}/1c/push-script/${kind}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const filename = kind === 'bsl' ? 'AinananPushOrders.bsl' : '1C_PUSH_SCRIPT.md';
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(`${filename} скачан`);
    } catch (e) {
      toast.error('Не удалось скачать файл');
    }
  };

  const copy1CBslToClipboard = async () => {
    try {
      const resp = await axios.get(`${API}/1c/push-script/bsl`, {
        headers: getAuthHeaders(),
        responseType: 'text',
      });
      // If server sent raw bytes, resp.data is a string here (responseType text).
      const text = typeof resp.data === 'string' ? resp.data : String(resp.data || '');
      if (!text) throw new Error('empty');
      // Prefer clipboard API; fall back to hidden textarea for older browsers.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Код 1С скопирован — вставьте в модуль регламента');
    } catch (e) {
      toast.error('Не удалось скопировать. Скачайте файл вместо этого.');
    }
  };

  return (
    <div className="space-y-4" data-testid="incoming-1c-page">
      {/* Download panel for 1C script */}
      <Card className="border-sky-200 bg-sky-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
              <FileCode className="w-5 h-5 text-sky-700" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-medium text-sky-950">Скрипт для 1С (push-back)</div>
              <div className="text-sm text-sky-800 mt-0.5">
                Внешняя обработка <b>AinananPushOrders</b> в стиле существующего pull-скрипта:
                двухшаговая авторизация <code>/1c/login</code> → <code>/1c/order/upsert</code>, HTTPS через
                <code> ЗащищенноеСоединениеOpenSSL</code>, регламент 5–10 мин.
                Нажмите <b>«Скопировать код»</b> и вставьте в модуль объекта обработки в Конфигураторе,
                затем сохраните как <code>.epf</code>. Инструкция ниже — со сборкой <code>.epf</code> за 15 минут.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="bg-sky-700 hover:bg-sky-800 text-white"
                onClick={copy1CBslToClipboard}
                data-testid="copy-1c-bsl-btn"
                title="Скопировать код в буфер, чтобы вставить в модуль регламента 1С"
              >
                <ClipboardCopy className="w-4 h-4 mr-1" />
                Скопировать код
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-sky-300 text-sky-900 hover:bg-sky-100"
                onClick={() => download1CFile('bsl')}
                data-testid="download-1c-bsl-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Код 1С (.bsl)
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-sky-300 text-sky-900 hover:bg-sky-100"
                onClick={() => download1CFile('instructions')}
                data-testid="download-1c-instructions-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Инструкция (.md)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Inbox className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Входящие из 1С</CardTitle>
                <p className="text-sm text-gray-500">Накладные из 1С, для которых не удалось найти клиента на сайте</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {[
                  { v: 'pending', label: 'Новые' },
                  { v: 'attached', label: 'Обработаны' },
                  { v: 'ignored', label: 'Игнор' },
                  { v: 'all', label: 'Все' },
                ].map(f => (
                  <button
                    key={f.v}
                    type="button"
                    onClick={() => setStatusFilter(f.v)}
                    className={`px-3 py-1 text-xs rounded border ${
                      statusFilter === f.v
                        ? 'bg-emerald-700 text-white border-emerald-700'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-300'
                    }`}
                    data-testid={`incoming-1c-filter-${f.v}`}
                  >{f.label}</button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={fetchRows} data-testid="incoming-1c-refresh">
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Обновить
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>Нет накладных в этом статусе</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="incoming-1c-list">
              {rows.map((r) => (
                <div key={r.id} className="border rounded-lg p-4 hover:shadow-sm transition" data-testid={`incoming-1c-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[280px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-gray-500">#{r.order_number_1c}</span>
                        {badge(r.status)}
                        <Badge className={r.role_hint === 'B2B' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                          {r.role_hint === 'B2B' ? <Building2 className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
                          {r.role_hint}
                        </Badge>
                        <span className="text-sm font-semibold text-gray-900 ml-2">{formatCurrency(r.total_amount)}</span>
                      </div>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <div className="font-medium text-gray-900">{r.customer_name || '—'}</div>
                        {r.bin_iin && <div className="text-gray-600">БИН: <span className="font-mono">{r.bin_iin}</span></div>}
                        {r.phone && <div className="text-gray-600 flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</div>}
                        {r.address && <div className="text-gray-600 flex items-center gap-1"><MapPin className="w-3 h-3" />{r.address}</div>}
                      </div>
                      {r.items && r.items.length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          {r.items.length} позиция{r.items.length > 1 ? 'й' : ''}: {r.items.slice(0, 3).map(i => i.ProductName || i.product_name).filter(Boolean).join(', ')}
                          {r.items.length > 3 ? '…' : ''}
                        </div>
                      )}
                      {r.resolution_note && (
                        <div className="mt-2 text-xs italic text-gray-500">{r.resolution_note}</div>
                      )}
                    </div>
                    {r.status === 'pending' && (
                      <div className="flex flex-col gap-2 min-w-[200px]">
                        <Button
                          size="sm"
                          className="bg-emerald-700 hover:bg-emerald-800 text-white"
                          onClick={() => { setAttachTarget(r); setSearchQ(''); setSearchResults([]); }}
                          data-testid={`incoming-1c-attach-${r.id}`}
                        >
                          <Link2 className="w-4 h-4 mr-1" />
                          Привязать к клиенту
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                          onClick={() => doCreateClient(r)}
                          data-testid={`incoming-1c-create-${r.id}`}
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          Создать нового
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-gray-500 hover:text-red-700"
                          onClick={() => doIgnore(r)}
                          data-testid={`incoming-1c-ignore-${r.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Игнорировать
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attach dialog */}
      <Dialog open={!!attachTarget} onOpenChange={(v) => { if (!v) { setAttachTarget(null); setSearchQ(''); setSearchResults([]); } }}>
        <DialogContent className="max-w-2xl" data-testid="incoming-1c-attach-dialog">
          <DialogHeader>
            <DialogTitle>Привязать накладную {attachTarget?.order_number_1c} к клиенту</DialogTitle>
            <DialogDescription>
              Найдите существующего клиента по имени, e-mail, телефону или БИН.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Поиск</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  className="pl-9"
                  placeholder="Название компании, e-mail, телефон или БИН"
                  value={searchQ}
                  onChange={(e) => { setSearchQ(e.target.value); if (attachTarget) searchUsers(attachTarget.id, e.target.value); }}
                  data-testid="incoming-1c-search-input"
                />
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto border rounded-md" data-testid="incoming-1c-search-results">
              {searching && <div className="text-center py-4 text-sm text-gray-400">Ищем…</div>}
              {!searching && searchResults.length === 0 && searchQ.length >= 2 && (
                <div className="text-center py-4 text-sm text-gray-400">Ничего не найдено</div>
              )}
              {searchResults.map(u => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => doAttach(u.id)}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-b-0"
                  data-testid={`incoming-1c-search-user-${u.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">
                        {u.company_name || u.full_name || u.email}
                      </div>
                      <div className="text-xs text-gray-500">
                        {u.role} · {u.email}{u.phone ? ` · ${u.phone}` : ''}{u.tax_id ? ` · БИН ${u.tax_id}` : ''}
                      </div>
                    </div>
                    <Link2 className="w-4 h-4 text-emerald-600" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
